import { useState, FormEvent, useMemo, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useAuthStore } from '@/stores/authStore';
import { isWebAuthnAvailable } from '@/utils/webauthn';
import { Loader2, Fingerprint, User, Mail, ArrowRight, AlertTriangle, Check, Key, Lock } from 'lucide-react';
import gsap from 'gsap';
import clsx from 'clsx';
import arcReactorIcon from '@/assets/arc-reactor-login.png';

// ── HUD Bezel Frame ──────────────────────────────────────────────────
// Clean beveled frame with top/side recesses and accent details.

function HudBezel({ children }: { children: React.ReactNode }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [dims, setDims] = useState({ w: 0, h: 0 });

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => setDims({ w: el.offsetWidth, h: el.offsetHeight });
    update();
    const ro = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      setDims({ w: Math.round(width), h: Math.round(height) });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const { w, h } = dims;

  const BVL = 14;       // corner bevel (all 4 corners)
  // Header recess (trapezoidal dip at top center)
  const RH = 90, RD = 6, RA = 14;
  // Side recesses (trapezoidal indent on left/right walls)
  const SRH = 50, SRD = 6, SRA = 14;

  const bezelPath = w > 0 ? [
    // Start at TL bevel, clockwise
    `M ${BVL} 0`,
    // Top edge → header recess
    `L ${w / 2 - RH} 0`,
    `L ${w / 2 - RH + RA} ${RD}`,
    `L ${w / 2 + RH - RA} ${RD}`,
    `L ${w / 2 + RH} 0`,
    // Top edge → TR bevel
    `L ${w - BVL} 0`,
    `L ${w} ${BVL}`,
    // Right wall → side recess
    `L ${w} ${h / 2 - SRH}`,
    `L ${w - SRD} ${h / 2 - SRH + SRA}`,
    `L ${w - SRD} ${h / 2 + SRH - SRA}`,
    `L ${w} ${h / 2 + SRH}`,
    // Right wall → BR bevel
    `L ${w} ${h - BVL}`, `L ${w - BVL} ${h}`,
    // Bottom edge → BL bevel
    `L ${BVL} ${h}`, `L 0 ${h - BVL}`,
    // Left wall → side recess (going up)
    `L 0 ${h / 2 + SRH}`,
    `L ${SRD} ${h / 2 + SRH - SRA}`,
    `L ${SRD} ${h / 2 - SRH + SRA}`,
    `L 0 ${h / 2 - SRH}`,
    // Left wall → TL bevel
    `L 0 ${BVL}`,
    'Z',
  ].join(' ') : '';

  return (
    <div ref={containerRef} className="relative">
      {w > 0 && (
        <svg
          className="absolute inset-0 w-full h-full pointer-events-none"
          viewBox={`-6 -6 ${w + 12} ${h + 12}`}
          style={{ filter: 'drop-shadow(0 0 10px rgba(0, 212, 255, 0.12))' }}
        >
          <defs>
            <linearGradient id="bezelFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="rgba(4, 6, 14, 0.82)" />
              <stop offset="100%" stopColor="rgba(2, 4, 10, 0.88)" />
            </linearGradient>
            <linearGradient id="beamGlow" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="transparent" />
              <stop offset="40%" stopColor="rgba(0, 212, 255, 0.8)" />
              <stop offset="60%" stopColor="rgba(0, 212, 255, 0.8)" />
              <stop offset="100%" stopColor="transparent" />
            </linearGradient>
          </defs>

          {/* Background fill */}
          <path d={bezelPath} fill="url(#bezelFill)" />
          {/* Static border (faint) */}
          <path d={bezelPath} fill="none" stroke="rgba(0, 212, 255, 0.15)" strokeWidth="1.5" />
          {/* Beam animation — traveling highlight along border */}
          <path
            d={bezelPath}
            fill="none"
            stroke="rgba(0, 212, 255, 0.8)"
            strokeWidth="2"
            strokeDasharray={`${Math.round((w + h) * 0.15)} ${Math.round((w + h) * 2.5)}`}
            strokeLinecap="round"
            style={{ filter: 'drop-shadow(0 0 6px rgba(0, 212, 255, 0.7)) drop-shadow(0 0 12px rgba(0, 212, 255, 0.3))' }}
          >
            <animate
              attributeName="stroke-dashoffset"
              from="0"
              to={`-${Math.round((w + h) * 2.7)}`}
              dur="6s"
              repeatCount="indefinite"
            />
          </path>

          {/* ── Recess accent lines ── */}
          <line x1={w / 2 - RH + RA + 8} y1={RD}
                x2={w / 2 + RH - RA - 8} y2={RD}
                stroke="rgba(0, 212, 255, 0.1)" strokeWidth="0.5" />
          <line x1={w - SRD} y1={h / 2 - SRH + SRA + 6}
                x2={w - SRD} y2={h / 2 + SRH - SRA - 6}
                stroke="rgba(0, 212, 255, 0.08)" strokeWidth="0.5" />
          <line x1={SRD} y1={h / 2 - SRH + SRA + 6}
                x2={SRD} y2={h / 2 + SRH - SRA - 6}
                stroke="rgba(0, 212, 255, 0.08)" strokeWidth="0.5" />

          {/* ── Structural tick marks along edges ── */}
          <line x1={w - 1} y1={BVL + 16} x2={w + 2} y2={BVL + 16}
                stroke="rgba(0, 212, 255, 0.08)" strokeWidth="0.5" />
          <line x1={w - 1} y1={h - BVL - 16} x2={w + 2} y2={h - BVL - 16}
                stroke="rgba(0, 212, 255, 0.08)" strokeWidth="0.5" />
          <line x1={-2} y1={BVL + 16} x2={1} y2={BVL + 16}
                stroke="rgba(0, 212, 255, 0.08)" strokeWidth="0.5" />
          <line x1={-2} y1={h - BVL - 16} x2={1} y2={h - BVL - 16}
                stroke="rgba(0, 212, 255, 0.08)" strokeWidth="0.5" />
        </svg>
      )}
      <div className="relative z-10">{children}</div>
    </div>
  );
}

// Diagonal accent marks for cut-corner buttons (matches widget style)
// Buttons use 6px clip, so accents are 6px SVGs at the cut corners
function BtnAccents({ color = 'rgba(0,212,255,0.2)' }: { color?: string }) {
  return (
    <>
      <svg className="absolute -top-px -right-px w-[9px] h-[9px] pointer-events-none" viewBox="0 0 9 9" fill="none">
        <line x1="0" y1="0" x2="9" y2="9" stroke={color} strokeWidth="1.2" />
      </svg>
      <svg className="absolute -bottom-px -left-px w-[9px] h-[9px] pointer-events-none" viewBox="0 0 9 9" fill="none">
        <line x1="0" y1="0" x2="9" y2="9" stroke={color} strokeWidth="1.2" />
      </svg>
    </>
  );
}

type AuthStep = 'identify' | 'authenticate' | 'register' | 'totp';

export default function AuthPage() {
  const [searchParams] = useSearchParams();
  const shtEscapeHatch = searchParams.get('sht') === '1';
  const [step, setStep] = useState<AuthStep>('identify');
  const [identifier, setIdentifier] = useState('');
  const [username, setUsername] = useState('');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [existingUsername, setExistingUsername] = useState('');
  const [existingFullName, setExistingFullName] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [setupToken, setSetupToken] = useState('');
  const [shtVerified, setShtVerified] = useState(false);
  const [shtChecking, setShtChecking] = useState(false);
  const [totpToken, setTotpToken] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [userHasTotp, setUserHasTotp] = useState(false);
  const [pendingTotpCode, setPendingTotpCode] = useState('');

  const { lookup, passkeyRegister, passkeyLogin, verifyTOTPLogin } = useAuth();
  const navigate = useNavigate();

  const webauthnSupported = useMemo(() => isWebAuthnAvailable(), []);
  const containerRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const passkeyAutoTriggered = useRef(false);
  const totpAutoSubmitted = useRef(false);

  // SHT reveal gesture: triple-click the big Fingerprint icon + double-click
  // "AUTHENTICATION PROTOCOL v3.0" text, both within a 5-second window.
  const shtFpClicks = useRef<number[]>([]);
  const shtAuthClicks = useRef<number[]>([]);
  const [shtRevealed, setShtRevealed] = useState(!!shtEscapeHatch);
  const recordShtFp = useCallback(() => {
    const now = Date.now();
    shtFpClicks.current.push(now);
    const win = 5_000;
    const fp = shtFpClicks.current.filter(t => now - t < win);
    const auth = shtAuthClicks.current.filter(t => now - t < win);
    if (fp.length >= 3 && auth.length >= 2) setShtRevealed(true);
  }, []);
  const recordShtAuth = useCallback(() => {
    const now = Date.now();
    shtAuthClicks.current.push(now);
    const win = 5_000;
    const fp = shtFpClicks.current.filter(t => now - t < win);
    const auth = shtAuthClicks.current.filter(t => now - t < win);
    if (fp.length >= 3 && auth.length >= 2) setShtRevealed(true);
  }, []);

  // Navigate back one step
  const goBack = useCallback(() => {
    setError('');
    if (step === 'totp') {
      setTotpCode('');
      setTotpToken('');
      setPendingTotpCode('');
      setStep('identify');
    } else if (step === 'authenticate') {
      setStep('identify');
    } else if (step === 'register') {
      setStep('identify');
    }
  }, [step]);

  // Push browser history state on step change (enables two-finger swipe back)
  useEffect(() => {
    if (step !== 'identify') {
      window.history.pushState({ authStep: step }, '');
    }
  }, [step]);

  // Listen for popstate (browser back / two-finger trackpad swipe)
  useEffect(() => {
    const onPop = () => {
      if (step !== 'identify') goBack();
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [step, goBack]);

  // Escape key goes back
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && step !== 'identify') {
        e.preventDefault();
        goBack();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [step, goBack]);

  // Auto-trigger passkey popup when arriving at authenticate step
  useEffect(() => {
    if (step === 'authenticate' && !isLoading && !passkeyAutoTriggered.current) {
      passkeyAutoTriggered.current = true;
      const handler = pendingTotpCode ? handleAuthenticateWithTotp : handleAuthenticate;
      // Small delay so the UI renders first
      const t = setTimeout(handler, 300);
      return () => clearTimeout(t);
    }
    if (step !== 'authenticate') {
      passkeyAutoTriggered.current = false;
    }
  }, [step]);

  // Auto-submit TOTP when 6 digits are entered
  useEffect(() => {
    if (totpCode.length === 6 && !isLoading && !totpAutoSubmitted.current) {
      totpAutoSubmitted.current = true;
      // Small delay so the user sees the last digit appear
      const t = setTimeout(() => {
        const fakeEvent = { preventDefault: () => {} } as FormEvent;
        handleTOTPVerify(fakeEvent);
      }, 150);
      return () => clearTimeout(t);
    }
    if (totpCode.length < 6) {
      totpAutoSubmitted.current = false;
    }
  }, [totpCode, isLoading]);

  // GSAP boot-up sequence
  useEffect(() => {
    const tl = gsap.timeline();

    if (headerRef.current) {
      tl.fromTo(
        headerRef.current,
        { opacity: 0, y: -10 },
        { opacity: 1, y: 0, duration: 0.5, ease: 'power3.out' },
        0.2,
      );
    }

    if (cardRef.current) {
      tl.fromTo(
        cardRef.current,
        { opacity: 0, scale: 0.95, y: 15 },
        { opacity: 1, scale: 1, y: 0, duration: 0.6, ease: 'power3.out' },
        0.35,
      );
    }

    if (footerRef.current) {
      tl.fromTo(
        footerRef.current,
        { opacity: 0, y: 10 },
        { opacity: 1, y: 0, duration: 0.4, ease: 'power3.out' },
        0.55,
      );
    }
  }, []);

  // Particles
  const particles = useMemo(
    () =>
      Array.from({ length: 20 }, (_, i) => ({
        id: i,
        left: `${(i * 17 + 3) % 100}%`,
        top: `${(i * 23 + 7) % 100}%`,
        size: 1 + (i % 3),
        duration: `${8 + (i % 12)}s`,
        delay: `${(i * 0.4) % 5}s`,
      })),
    [],
  );

  const stepIndex = step === 'identify' ? 0 : 1; // totp counts as step 1 too

  const handleIdentify = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    const trimmed = identifier.trim();
    if (!trimmed) {
      setError('Please enter your email or username.');
      return;
    }
    setIsLoading(true);
    try {
      const result = await lookup(trimmed);
      if (result.exists) {
        setExistingUsername(result.username || trimmed);
        setExistingFullName(result.full_name || result.username || trimmed);
        setUserHasTotp(!!result.totp_enabled);
        if (result.totp_enabled) {
          // Skip TOTP step if we have a device trust token (backend validates it)
          const deviceTrust = localStorage.getItem('jarvis_device_trust');
          if (deviceTrust) {
            setStep('authenticate');
          } else {
            setTotpCode('');
            setStep('totp');
          }
        } else {
          setStep('authenticate');
        }
      } else {
        if (trimmed.includes('@')) setEmail(trimmed);
        else setUsername(trimmed);
        setStep('register');
      }
    } catch {
      setError('Connection error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAuthenticate = async () => {
    setError('');
    setIsLoading(true);
    try {
      const result = await passkeyLogin(identifier.trim());
      if (result && 'needs_totp' in result && result.needs_totp) {
        setTotpToken(result.totp_token);
        setTotpCode('');
        setStep('totp');
      } else {
        const pendingRedirect = localStorage.getItem('jarvis_post_login_redirect');
        if (pendingRedirect) {
          localStorage.removeItem('jarvis_post_login_redirect');
          navigate(pendingRedirect);
        } else {
          navigate('/');
        }
      }
    } catch (err: any) {
      const detail = err?.response?.data?.detail || err?.message || 'Authentication failed.';
      setError(detail);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTOTPVerify = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    if (totpCode.length !== 6) {
      setError('Enter the 6-digit code from your authenticator.');
      return;
    }

    // If we already have a totp_token (post-passkey flow), verify directly
    if (totpToken) {
      setIsLoading(true);
      try {
        await verifyTOTPLogin(totpToken, totpCode);
        const pendingRedirect = localStorage.getItem('jarvis_post_login_redirect');
        if (pendingRedirect) {
          localStorage.removeItem('jarvis_post_login_redirect');
          navigate(pendingRedirect);
        } else {
          navigate('/');
        }
      } catch (err: any) {
        const detail = err?.response?.data?.detail || err?.message || 'Invalid code.';
        setError(detail);
        setTotpCode('');
      } finally {
        setIsLoading(false);
      }
      return;
    }

    // Pre-passkey flow: store code, proceed to passkey auth
    setPendingTotpCode(totpCode);
    setStep('authenticate');
  };

  const handleAuthenticateWithTotp = async () => {
    setError('');
    setIsLoading(true);
    try {
      const result = await passkeyLogin(identifier.trim());
      if (result && 'needs_totp' in result && result.needs_totp) {
        // Now verify the TOTP code we collected earlier
        await verifyTOTPLogin(result.totp_token, pendingTotpCode);
        const pendingRedirect = localStorage.getItem('jarvis_post_login_redirect');
        if (pendingRedirect) {
          localStorage.removeItem('jarvis_post_login_redirect');
          navigate(pendingRedirect);
        } else {
          navigate('/');
        }
      } else {
        const pendingRedirect = localStorage.getItem('jarvis_post_login_redirect');
        if (pendingRedirect) {
          localStorage.removeItem('jarvis_post_login_redirect');
          navigate(pendingRedirect);
        } else {
          navigate('/');
        }
      }
    } catch (err: any) {
      const detail = err?.response?.data?.detail || err?.message || 'Authentication failed.';
      setError(detail);
      if (detail.toLowerCase().includes('totp') || detail.toLowerCase().includes('code')) {
        // TOTP code was wrong, go back to TOTP step
        setPendingTotpCode('');
        setTotpCode('');
        setTotpToken('');
        setStep('totp');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    const emailVal = email.trim();
    const usernameVal = username.trim();

    if (!emailVal || !usernameVal) {
      setError('Email and username are required.');
      return;
    }
    if (!emailVal.includes('@')) {
      setError('Please enter a valid email.');
      return;
    }
    if (usernameVal.length < 3) {
      setError('Username must be at least 3 characters.');
      return;
    }

    setIsLoading(true);
    try {
      await passkeyRegister(emailVal, usernameVal, fullName || undefined, setupToken.trim());
      const pendingRedirect = localStorage.getItem('jarvis_post_login_redirect');
      if (pendingRedirect) {
        localStorage.removeItem('jarvis_post_login_redirect');
        navigate(pendingRedirect);
      } else {
        navigate('/');
      }
    } catch (err: any) {
      const detail = err?.response?.data?.detail || err?.message || 'Registration failed.';
      setError(detail);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifySht = async () => {
    setShtChecking(true);
    setError('');
    try {
      // Use the register endpoint's x-setup-token header pattern to verify
      const resp = await fetch(
        `${import.meta.env.VITE_API_URL || ''}/api/v1/auth/verify-setup-token`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ setup_token: setupToken.trim() }),
        }
      );
      if (resp.ok) {
        setShtVerified(true);
      } else {
        setError('Invalid System Handshake Token.');
        setShtVerified(false);
      }
    } catch {
      setError('Could not verify token. Please try again.');
      setShtVerified(false);
    } finally {
      setShtChecking(false);
    }
  };

  return (
    <div ref={containerRef} className="relative min-h-screen w-full flex items-center justify-center bg-black overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Grid */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(0, 212, 255, 0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(0, 212, 255, 0.5) 1px, transparent 1px)',
            backgroundSize: '60px 60px',
            willChange: 'transform',
          }}
        />
        {/* Radial glow */}
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full"
          style={{
            background:
              'radial-gradient(circle, rgba(0, 212, 255, 0.06) 0%, rgba(0, 128, 255, 0.03) 40%, transparent 70%)',
          }}
        />
        {/* Particles */}
        {particles.map((p) => (
          <div
            key={p.id}
            className="absolute rounded-full bg-jarvis-blue/20"
            style={{
              left: p.left,
              top: p.top,
              width: `${p.size}px`,
              height: `${p.size}px`,
              animation: `floatParticle ${p.duration} ease-in-out infinite`,
              animationDelay: p.delay,
            }}
          />
        ))}
      </div>


      {/* Corner brackets */}
      {(['tl', 'tr', 'bl', 'br'] as const).map((pos) => {
        const isTop = pos.startsWith('t');
        const isLeft = pos.endsWith('l');
        return (
          <div
            key={pos}
            className="absolute pointer-events-none z-10"
            style={{ [isTop ? 'top' : 'bottom']: '16px', [isLeft ? 'left' : 'right']: '16px' }}
          >
            <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
              <path
                d={
                  isTop && isLeft
                    ? 'M0 16 L0 0 L16 0'
                    : isTop && !isLeft
                      ? 'M24 0 L40 0 L40 16'
                      : !isTop && isLeft
                        ? 'M0 24 L0 40 L16 40'
                        : 'M24 40 L40 40 L40 24'
                }
                stroke="rgba(0, 212, 255, 0.15)"
                strokeWidth="1"
              />
            </svg>
          </div>
        );
      })}

      {/* Error pill — floating at top */}
      {error && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-50 px-5 py-2 rounded-full bg-hud-red/10 border border-hud-red/25 text-hud-red text-xs font-mono text-center max-w-md animate-[fadeIn_0.2s_ease-out]">
          {error.replace(/\s*See:.*$/i, '')}
        </div>
      )}

      {/* Auth Card */}
      <div className="relative z-10 w-full max-w-[520px] mx-5">
        {/* Header label */}
        <div ref={headerRef} className="text-center mb-5 opacity-0">
          <span className="hud-label text-[10px]">STARK INDUSTRIES — SECURE ACCESS TERMINAL</span>
        </div>

        {/* Main panel — HUD bezel frame */}
        <div ref={cardRef} className="opacity-0">
        <HudBezel>
          <div className="px-8 py-8 max-w-sm mx-auto">
          {/* Branding */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-14 h-14 mb-3 hud-clip-sm bg-gradient-to-br from-jarvis-blue/15 to-blue-500/10 border border-jarvis-blue/10">
              {step === 'totp' ? (
                <Lock size={24} className="text-jarvis-blue" />
              ) : step === 'authenticate' ? (
                <Fingerprint size={24} className="text-jarvis-blue cursor-default select-none" onClick={recordShtFp} />
              ) : (
                <img src={arcReactorIcon} alt="Arc Reactor" className="w-[34px] h-[34px] object-contain" style={{ filter: 'drop-shadow(0 0 4px rgba(0, 212, 255, 0.5))' }} />
              )}
            </div>
            <h1 className="text-xl font-display font-bold tracking-[0.2em] text-jarvis-blue glow-text">
              J.A.R.V.I.S.
            </h1>
            <p className="text-[11px] text-gray-500 mt-1 tracking-wider font-mono select-none cursor-default" onClick={recordShtAuth}>
              AUTHENTICATION PROTOCOL v3.0
            </p>
          </div>

          {/* Progress */}
          <div className="flex items-center gap-2 mb-6 px-4">
            <div
              className={clsx('h-0.5 flex-1 rounded-full transition-all duration-500', {
                'bg-jarvis-blue': stepIndex >= 0,
                'bg-gray-800': stepIndex < 0,
              })}
            />
            <div
              className={clsx('w-1.5 h-1.5 rounded-full transition-all duration-500', {
                'bg-jarvis-blue shadow-[0_0_6px_rgba(0,212,255,0.5)]': stepIndex >= 0,
                'bg-gray-800': stepIndex < 0,
              })}
            />
            <div
              className={clsx('h-0.5 flex-1 rounded-full transition-all duration-500', {
                'bg-jarvis-blue': stepIndex >= 1,
                'bg-gray-800': stepIndex < 1,
              })}
            />
            <div
              className={clsx('w-1.5 h-1.5 rounded-full transition-all duration-500', {
                'bg-jarvis-blue shadow-[0_0_6px_rgba(0,212,255,0.5)]': stepIndex >= 1,
                'bg-gray-800': stepIndex < 1,
              })}
            />
            <div
              className={clsx('h-0.5 flex-1 rounded-full transition-all duration-500', {
                'bg-jarvis-blue': step !== 'identify',
                'bg-gray-800': step === 'identify',
              })}
            />
          </div>

          {/* WebAuthn warning */}
          {!webauthnSupported && (
            <div className="mb-4 px-4 py-2.5 hud-clip-sm bg-hud-amber/10 border border-hud-amber/20 flex items-center gap-2">
              <AlertTriangle size={14} className="text-hud-amber flex-shrink-0" />
              <span className="text-xs text-hud-amber">Passkeys not supported in this browser.</span>
            </div>
          )}

          {/* SHT escape hatch — top-level when ?sht=1 in URL. Bypasses
              the entire passkey flow so the user can log in with username
              + SHT directly, regardless of which step they're on. */}
          {shtEscapeHatch && (
            <ShtDirectLogin onError={setError} />
          )}

          {/* Step: Identify */}
          {!shtEscapeHatch && step === 'identify' && (
            <form onSubmit={handleIdentify} className="space-y-4">
              <div>
                <label className="hud-label block mb-2">IDENTIFICATION</label>
                <div className="relative">
                  <Mail
                    size={14}
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-jarvis-blue/40"
                  />
                  <input
                    type="text"
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                    placeholder="Email or username"
                    className="w-full jarvis-input pl-10 pr-4 py-3 text-sm font-mono"
                    autoComplete="username webauthn"
                    autoFocus
                    disabled={isLoading}
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading || !identifier.trim()}
                className="jarvis-button-gold w-full py-3 text-sm font-display font-semibold tracking-wider uppercase flex items-center justify-center gap-2"
                style={{ opacity: isLoading || !identifier.trim() ? 0.4 : 1 }}
              >
                {isLoading ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <>
                    Continue
                    <ArrowRight size={14} />
                  </>
                )}
              </button>
            </form>
          )}

          {/* Step: Authenticate (Passkey) */}
          {!shtEscapeHatch && step === 'authenticate' && (
            <div className="space-y-4">
              <div className="text-center">
                <p className="text-sm text-gray-300">
                  Welcome back,{' '}
                  <span className="text-jarvis-gold font-semibold">{existingFullName}</span>
                </p>
                <p className="text-xs text-gray-500 mt-1 select-none cursor-default" onClick={recordShtAuth}>
                  Authenticate with your passkey
                </p>
              </div>

              <button
                onClick={pendingTotpCode ? handleAuthenticateWithTotp : handleAuthenticate}
                disabled={isLoading}
                className="relative jarvis-button w-full py-3 text-sm font-display font-semibold tracking-wider uppercase flex items-center justify-center gap-2"
                style={{ opacity: isLoading ? 0.5 : 1 }}
              >
                <BtnAccents />
                {isLoading ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <>
                    <Fingerprint size={16} />
                    Verify Identity
                  </>
                )}
              </button>

              {shtRevealed && (
                <ShtFallback identifier={identifier} onError={setError} initialOpen />
              )}
            </div>
          )}

          {/* Step: TOTP Verification */}
          {!shtEscapeHatch && step === 'totp' && (
            <div className="space-y-4">
              <div className="text-center">
                <p className="text-sm text-gray-300">Two-Factor Authentication</p>
                <p className="text-xs text-gray-500 mt-1">Enter the code from your authenticator app</p>
              </div>

              <div>
                <label className="hud-label block mb-2">VERIFICATION CODE</label>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="w-full jarvis-input px-4 py-3 text-center text-lg font-mono tracking-[0.5em]"
                  autoFocus
                  autoComplete="one-time-code"
                  disabled={isLoading}
                />
              </div>

              {isLoading && (
                <div className="flex justify-center">
                  <Loader2 size={20} className="animate-spin text-jarvis-blue" />
                </div>
              )}
            </div>
          )}

          {/* Step: Register */}
          {!shtEscapeHatch && step === 'register' && (
            <form onSubmit={handleRegister} className="space-y-3">
              <div className="text-center mb-2">
                <p className="text-xs text-gray-400">New user detected. Complete your profile.</p>
              </div>

              <div>
                <label className="hud-label block mb-1.5">EMAIL</label>
                <div className="relative">
                  <Mail
                    size={14}
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-jarvis-blue/40"
                  />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="stark@avengers.com"
                    className="w-full jarvis-input pl-10 pr-4 py-2.5 text-sm font-mono"
                    autoComplete="email"
                    disabled={isLoading}
                  />
                </div>
              </div>

              <div>
                <label className="hud-label block mb-1.5">USERNAME</label>
                <div className="relative">
                  <User
                    size={14}
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-jarvis-blue/40"
                  />
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="tonystark"
                    className="w-full jarvis-input pl-10 pr-4 py-2.5 text-sm font-mono"
                    autoComplete="username"
                    disabled={isLoading}
                  />
                </div>
              </div>

              <div>
                <label className="hud-label block mb-1.5">
                  DISPLAY NAME <span className="text-gray-600">(OPTIONAL)</span>
                </label>
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Tony Stark"
                  className="w-full jarvis-input px-4 py-2.5 text-sm"
                  disabled={isLoading}
                />
              </div>

              <div>
                <label className="hud-label block mb-1.5">
                  SYSTEM HANDSHAKE TOKEN
                </label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Key
                      size={14}
                      className="absolute left-4 top-1/2 -translate-y-1/2 text-jarvis-blue/40"
                    />
                    <input
                      type="password"
                      value={setupToken}
                      onChange={(e) => { setSetupToken(e.target.value); setShtVerified(false); }}
                      placeholder="Enter SHT"
                      className="w-full jarvis-input pl-10 pr-4 py-2.5 text-sm font-mono"
                      disabled={isLoading || shtVerified}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleVerifySht}
                    disabled={!setupToken.trim() || shtChecking || shtVerified}
                    className={clsx(
                      'w-11 h-11 flex items-center justify-center hud-clip-sm border transition-all flex-shrink-0',
                      shtVerified
                        ? 'bg-hud-green/15 border-hud-green/30'
                        : 'glass-subtle border-jarvis-blue/30 hover:border-jarvis-blue/20',
                      (!setupToken.trim() || shtChecking) && !shtVerified && 'opacity-40'
                    )}
                  >
                    {shtChecking ? (
                      <Loader2 size={14} className="animate-spin text-jarvis-blue" />
                    ) : shtVerified ? (
                      <Check size={14} className="text-hud-green" />
                    ) : (
                      <ArrowRight size={14} className="text-jarvis-blue" />
                    )}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading || !email.trim() || !username.trim() || !shtVerified}
                className="relative jarvis-button-gold w-full py-3 text-sm font-display font-semibold tracking-wider uppercase flex items-center justify-center gap-2 mt-2"
                style={{
                  opacity: isLoading || !email.trim() || !username.trim() || !shtVerified ? 0.4 : 1,
                }}
              >
                <BtnAccents color="rgba(168,85,247,0.2)" />
                {isLoading ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <>
                    <Fingerprint size={16} />
                    Create Passkey & Register
                  </>
                )}
              </button>
            </form>
          )}
          </div>
        </HudBezel>
        </div>

        {/* Footer */}
        <div ref={footerRef} className="text-center mt-5 opacity-0">
          <div className="hud-divider mb-2">
            <div className="hud-divider-dot" />
          </div>
          <span className="hud-label text-[9px] text-gray-600">
            ENCRYPTED BIOMETRIC AUTHENTICATION
          </span>
        </div>
      </div>
    </div>
  );
}

// ── SHT fallback login (gated by hidden gesture) ───────────────────────
// Carter's spec: the fallback should NOT be visible by default. To
// reveal it, the user has to triple-click the fingerprint icon AND
// then double-click the "AUTHENTICATION" / "Authenticate" copy
// underneath, all within 5 seconds. That keeps the SHT path
// invisible to anyone glancing at the auth page while still letting
// the owner reach it on the .ai domain where they don't have a
// passkey yet. Once revealed, the inline form posts to
// /auth/cli-login (same as CLI), gets JWT tokens, and reloads.
function ShtGate({
  identifier,
  onError,
  initialRevealed,
  children,
}: {
  identifier: string;
  onError: (msg: string) => void;
  initialRevealed?: boolean;
  children: (
    onFingerprint: () => void,
    onAuthLabel: () => void,
  ) => React.ReactNode;
}) {
  const [revealed, setRevealed] = useState(!!initialRevealed);
  const fpClicks = useRef<number[]>([]);
  const authClicks = useRef<number[]>([]);

  // Chord gesture: triple-click the fingerprint icon AND double-click the
  // "Authenticate" label, both within a 5-second rolling window.
  const checkChord = (now: number) => {
    const window = 5_000;
    const fp = fpClicks.current.filter((t) => now - t < window);
    const auth = authClicks.current.filter((t) => now - t < window);
    if (fp.length >= 3 && auth.length >= 2) setRevealed(true);
  };
  const onFingerprint = () => {
    const now = Date.now();
    fpClicks.current.push(now);
    checkChord(now);
  };
  const onAuthLabel = () => {
    const now = Date.now();
    authClicks.current.push(now);
    checkChord(now);
  };

  return (
    <div className="space-y-4">
      {children(onFingerprint, onAuthLabel)}
      {revealed && (
        <ShtFallback identifier={identifier} onError={onError} initialOpen />
      )}
    </div>
  );
}

function ShtFallback({
  identifier,
  onError,
  initialOpen,
}: {
  identifier: string;
  onError: (msg: string) => void;
  initialOpen?: boolean;
}) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(!!initialOpen);
  const [sht, setSht] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    onError('');
    if (!sht || sht.length < 4) {
      onError('SHT cannot be empty.');
      return;
    }
    setBusy(true);
    try {
      const res = await fetch('/api/v1/auth/web-sht-login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: identifier, sht }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        onError(j.detail || 'Access denied.');
        setBusy(false);
        return;
      }
      const data = await res.json();
      useAuthStore.getState().login(data.user, data.access_token, data.refresh_token);
      const pending = localStorage.getItem('jarvis_post_login_redirect');
      localStorage.removeItem('jarvis_post_login_redirect');
      navigate(pending || '/', { replace: true });
    } catch (e: any) {
      onError(e?.message || 'Login failed.');
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="w-full text-[10px] text-gray-500 hover:text-jarvis-blue/70 mt-2 transition-colors"
      >
        No passkey on this domain? Sign in with SHT
      </button>
    );
  }

  return (
    <div className="space-y-2 mt-2">
      <input
        type="password"
        placeholder="Secure Handshake Token"
        value={sht}
        onChange={(e) => setSht(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') submit();
        }}
        className="w-full jarvis-input px-4 py-2.5 text-sm font-mono"
        autoFocus
      />
      <button
        onClick={submit}
        disabled={busy}
        className="w-full jarvis-button py-2 text-xs uppercase tracking-wider disabled:opacity-50"
      >
        {busy ? 'Verifying…' : 'Sign in with SHT'}
      </button>
      <button
        onClick={() => { setOpen(false); setSht(''); }}
        className="w-full text-[10px] text-gray-500 hover:text-gray-300 transition-colors"
      >
        Cancel
      </button>
    </div>
  );
}

// ── Direct SHT login (URL escape hatch ?sht=1) ─────────────────────────
// Standalone username + SHT form. Renders at the top of the auth page
// when ?sht=1 is in the URL, bypassing the entire passkey/identify flow.
// This is the demo-friendly path: paste link, type two values, in.
function ShtDirectLogin({ onError }: { onError: (msg: string) => void }) {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [sht, setSht] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    onError('');
    if (!username.trim()) {
      onError('Username required.');
      return;
    }
    if (!sht || sht.length < 4) {
      onError('SHT cannot be empty.');
      return;
    }
    setBusy(true);
    try {
      const res = await fetch('/api/v1/auth/web-sht-login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: username.trim(), sht }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        onError(j.detail || 'Access denied.');
        setBusy(false);
        return;
      }
      const data = await res.json();
      useAuthStore.getState().login(data.user, data.access_token, data.refresh_token);
      const pending = localStorage.getItem('jarvis_post_login_redirect');
      localStorage.removeItem('jarvis_post_login_redirect');
      navigate(pending || '/', { replace: true });
    } catch (e: any) {
      onError(e?.message || 'Login failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="text-center mb-2">
        <p className="hud-label text-[10px] text-jarvis-blue/70">SHT DIRECT ACCESS</p>
        <p className="text-[10px] text-gray-500 mt-1">
          Sign in with username + Secure Handshake Token. No passkey required.
        </p>
      </div>
      <div>
        <label className="hud-label block mb-2 text-[10px]">USERNAME</label>
        <input
          type="text"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && submit()}
          placeholder="mr.stark"
          autoComplete="username"
          autoFocus
          className="w-full jarvis-input px-4 py-2.5 text-sm font-mono"
        />
      </div>
      <div>
        <label className="hud-label block mb-2 text-[10px]">SHT</label>
        <input
          type="password"
          value={sht}
          onChange={(e) => setSht(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && submit()}
          placeholder="Secure Handshake Token"
          autoComplete="current-password"
          className="w-full jarvis-input px-4 py-2.5 text-sm font-mono"
        />
      </div>
      <button
        onClick={submit}
        disabled={busy}
        className="jarvis-button-gold w-full py-3 text-sm font-display font-semibold tracking-wider uppercase flex items-center justify-center gap-2 disabled:opacity-40"
      >
        {busy ? <Loader2 size={16} className="animate-spin" /> : 'AUTHENTICATE'}
      </button>
    </div>
  );
}
